'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  Clock,
  Eye,
  FileText,
  Loader2,
  PauseCircle,
  RotateCcw,
  ScanText,
  SkipForward,
  XCircle,
  ZoomIn
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PermissionGate } from '@/features/auth';
import { formatDuration } from '@/lib/format-duration';
import { useAuthenticatedBlobUrl } from '@/hooks/use-authenticated-blob-url';
import { API_ENDPOINTS } from '@/lib/api/config';
import { useContainerResultsLive } from '../hooks/use-container-results-live';
import { buildContainerPageRows } from '../utils/container-page-rows';
import { withFirstGroupPerResult } from '../utils/first-group-per-result';
import { resolvePageRowState } from '../utils/page-row-state';
import { resolveOwnedPages } from '../utils/owned-pages';
import { formatPageRanges } from '../utils/page-ranges';
import type { ResultsDeepLink } from '../utils/results-deep-link';
import { useStartOcrMutation } from '../services/ocr.mutations';
import { OcrTemplatePoolDialog } from './ocr-template-pool-dialog';
import { PageResultsDialog } from './page-results-dialog';
import { ContainerIdPrefix } from './container-id-prefix';
import { SuggestionHistoryPanel } from './suggestion-history-panel';
import { FailedBatchesCallout } from './failed-batches-callout';
import { OcrErrorCard } from './ocr-error-card';
import { type DisplayOcrStatus } from '../utils/run-status';
import type { OcrPage, OcrResult, ExtractedDataItem } from '../types';

// No `pending` arm: `displayOcrStatus` folds it into `processing`, matching
// the listing's collapsed `ocr_status`.
const STATUS_CONFIG: Record<
  DisplayOcrStatus,
  { icon: typeof CheckCircle; className: string }
> = {
  processing: {
    icon: Loader2,
    className:
      'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400'
  },
  paused: {
    icon: PauseCircle,
    className:
      'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400'
  },
  completed: {
    icon: CheckCircle,
    className:
      'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
  },
  completed_with_errors: {
    icon: XCircle,
    className:
      'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400'
  },
  failed: {
    icon: XCircle,
    className: 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400'
  },
  skipped: {
    icon: SkipForward,
    className: 'border-muted-foreground/30 bg-muted/10 text-muted-foreground'
  }
};

const COVERAGE_CONFIG: Record<
  'no_data' | 'not_covered',
  { icon: typeof CheckCircle; className: string }
> = {
  no_data: {
    icon: AlertTriangle,
    className:
      'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400'
  },
  not_covered: {
    icon: Clock,
    className: 'border-muted-foreground/30 bg-muted/10 text-muted-foreground'
  }
};

/**
 * The page's own scan, and the handle that opens its results dialog.
 *
 * `batchId` is the owning run's workload assignment — the thumbnail route
 * rasterizes PDF pages, so it is the only source that works for both image and
 * PDF scans. Both endpoints are Bearer-authenticated, hence the blob hook
 * rather than a bare `<img src>`; its shared LRU cache means the dialog's
 * full-size fetch of the same page is deduped per URL.
 *
 * A page with no image degrades to the file glyph — never a broken image box.
 * The visual language is `PageAssignmentRow`'s, so a page reads the same
 * wherever it is listed.
 */
function PageRowThumbnail({
  page,
  batchId,
  label,
  onOpen
}: {
  page: OcrPage;
  batchId: number | null;
  /** The row's own printed page label, so the alt text cannot disagree with it. */
  label: string;
  onOpen: () => void;
}) {
  const t = useTranslations('ocr');

  // `image_url` is null on local disk (only s3/gcs/public get signed), so
  // `has_image` is what says a page HAS bytes to stream; neither means there is
  // nothing to fetch and the hook skips the request entirely.
  const thumbUrl = useAuthenticatedBlobUrl(
    batchId != null && (page.image_url || page.has_image)
      ? API_ENDPOINTS.PHOTO_SCANNING.PAGE_THUMBNAIL(batchId, page.id)
      : null
  );

  return (
    <button
      type='button'
      onClick={onOpen}
      aria-label={t('openPageResults')}
      title={t('openPageResults')}
      className='bg-muted group focus-visible:ring-ring relative flex h-24 w-[4.5rem] shrink-0 items-center justify-center overflow-hidden rounded border outline-none focus-visible:ring-2'
    >
      {thumbUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={thumbUrl}
          alt={label}
          className='h-full w-full object-cover'
          loading='lazy'
          decoding='async'
        />
      ) : (
        <FileText className='text-muted-foreground/50 h-5 w-5' />
      )}
      <span className='bg-foreground/40 absolute inset-0 flex items-center justify-center text-white opacity-0 transition-opacity group-hover:opacity-100'>
        <ZoomIn className='h-4 w-4' />
      </span>
    </button>
  );
}

function ConfidenceDot({ rate }: { rate: number }) {
  const color =
    rate >= 0.95
      ? 'bg-emerald-500'
      : rate >= 0.8
        ? 'bg-blue-500'
        : 'bg-red-500';
  return (
    <span className={cn('inline-block h-1.5 w-1.5 rounded-full', color)} />
  );
}

function FieldQuickView({
  fields,
  resultId,
  pageNumber,
  onOpenField
}: {
  fields: ExtractedDataItem[];
  /** The run that read these fields — part of a field's deep-link identity. */
  resultId: number;
  /**
   * Global 1-based page these fields sit on — the rest of that identity. A
   * repeating template extracts the same key once per page, so the key alone
   * names three different fields on a three-page invoice.
   */
  pageNumber: number;
  /** Opens the extraction panel on one field, in-screen. */
  onOpenField: (target: ResultsDeepLink) => void;
}) {
  const t = useTranslations('ocr');

  if (fields.length === 0) return null;

  return (
    <div className='bg-muted/40 mt-2 rounded-md border'>
      <div className='divide-y'>
        {fields.map((field, index) => {
          const confidence = field.confidence_rate ?? 0;
          const isLow = confidence < 0.8;

          return (
            // A row opens the panel ON this field: expanded, active, and with
            // the document viewer scrolled to its highlight. Reading a value
            // here and then having to find it again in the panel was the whole
            // friction — and until OMA-635 the jump also cost a full route
            // change, so it is a button over this screen now, not a link off it.
            <button
              // `key` is NOT unique here: one page can carry the same field key
              // twice (a repeating template occurrence, or a free-form item
              // rediscovering a declared name), which React reported as two
              // children sharing the key `شروط_الدفع`. The position is what
              // makes a row distinct.
              key={`${field.key}-${field.page_number ?? pageNumber}-${index}`}
              type='button'
              onClick={() =>
                onOpenField({
                  kind: 'field',
                  resultId,
                  pageNumber: field.page_number ?? pageNumber,
                  fieldKey: field.key
                })
              }
              className='hover:bg-muted/70 flex w-full items-start gap-2 px-3 py-2 text-start transition-colors'
            >
              <div className='min-w-0 flex-1'>
                <div className='flex min-w-0 items-center gap-1.5'>
                  <span className='text-muted-foreground min-w-0 truncate text-[11px] font-medium uppercase'>
                    {field.key.replace(/_/g, ' ')}
                  </span>
                  <ConfidenceDot rate={confidence} />
                  {isLow && (
                    <AlertTriangle className='h-3 w-3 shrink-0 text-red-500' />
                  )}
                </div>
                {/* `dir='auto'` — the value is in the document's language, not
                    the UI's. Inherited RTL moves a leading numeric run to the
                    far end, so "90 days from invoice date" reads back as
                    "days from invoice date 90". */}
                <p dir='auto' className='mt-0.5 truncate text-sm'>
                  {field.value != null ? (
                    String(field.value)
                  ) : (
                    <span className='text-muted-foreground italic'>
                      {t('noValue')}
                    </span>
                  )}
                </p>
              </div>
              {field.status === 'processed' ? (
                <CheckCircle className='mt-1 h-3.5 w-3.5 shrink-0 text-emerald-500' />
              ) : (
                <Clock className='text-muted-foreground mt-1 h-3.5 w-3.5 shrink-0' />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface ContainerResultsSheetProps {
  containerId: number | null;
  containerLabel: string;
  /**
   * Registration the container belongs to. When provided, the empty-state
   * Start OCR button opens the template-pool dialog (AI suggestions +
   * candidate selection); without it the legacy direct start runs.
   */
  registrationId?: number;
  /**
   * Opens the extraction panel over the OCR screen, on a page or a field
   * (OMA-635). Every row here used to carry a `<Link>` to
   * `/dashboard/ocr-containers/<id>` instead — the same destination, but a
   * route change that tore the listing down behind the sheet.
   */
  onOpenResultsView: (containerId: number, target?: ResultsDeepLink) => void;
  onClose: () => void;
}

export function ContainerResultsSheet({
  containerId,
  containerLabel,
  registrationId,
  onOpenResultsView,
  onClose
}: ContainerResultsSheetProps) {
  const t = useTranslations('ocr');
  const startMutation = useStartOcrMutation();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [poolDialogOpen, setPoolDialogOpen] = useState(false);
  // Local state, NOT a hash overlay. This sheet is itself hash-driven
  // (`#results/<id>`) and only one hash overlay can be open at a time, so a
  // nested one would clobber its parent's key and close the sheet underneath
  // the dialog. The accepted trade-off is that browser Back closes the sheet
  // rather than the dialog — see `features/CLAUDE.md` § URL-Hash Overlays.
  const [dialogPageId, setDialogPageId] = useState<number | null>(null);

  const { data, isPending } = useContainerResultsLive(containerId);

  const results = (data?.data ?? []) as OcrResult[];

  // One row per PHYSICAL page — a container's results each repeat its whole
  // page list, so this must not be results × pages.
  // `isFirstGroupForResult` marks the ONE row a result-level affordance belongs
  // on — its error card, its failed-batch callout, its duration. That used to be
  // `pageIndex === 1`, which held only while every result was anchored to the
  // container's first page; once a per-page template assignment gave a run pages
  // 8-12, its error card had no row to render on and disappeared entirely. Same
  // reasoning, and same helper, as the results panel.
  const pageRows = useMemo(
    () => withFirstGroupPerResult(buildContainerPageRows(results)),
    [results]
  );

  // Page-level completion = this page produced data AND its run finished. The
  // `coverage` half is load-bearing: one run owns many pages, so counting rows
  // by their run's status marked every page of a completed run done, including
  // the ones it returned nothing for. Fields are counted per-page (bucketed via
  // chunk page_number); totals roll up across the whole container.
  const completedCount = pageRows.filter(
    (row) => row.coverage === 'extracted' && row.result.status === 'completed'
  ).length;
  const totalCount = pageRows.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const totalFields = pageRows.reduce((sum, row) => sum + row.fields.length, 0);
  const totalVerified = pageRows.reduce(
    (sum, row) =>
      sum + row.fields.filter((f) => f.status === 'processed').length,
    0
  );

  // Resolved from the live rows rather than stored alongside the id, so the
  // open dialog re-renders with the page's own fresh data as the run progresses.
  const dialogRow =
    pageRows.find((row) => row.page.id === dialogPageId) ?? null;

  // Binds the sheet's container to the listing's opener, so a row only has to
  // name the target. Guarded because `containerId` is null while the sheet is
  // closed — where no row exists to fire this anyway.
  function handleOpenResultsView(target: ResultsDeepLink) {
    if (containerId == null) return;
    onOpenResultsView(containerId, target);
  }

  async function handleStartOcr() {
    if (!containerId) return;
    if (registrationId) {
      setPoolDialogOpen(true);
      return;
    }
    try {
      await startMutation.mutateAsync({ containerId });
    } catch {
      // handled by global MutationCache
    }
  }

  return (
    <Sheet open={containerId != null} onOpenChange={() => onClose()}>
      <SheetContent className='flex w-full flex-col gap-0 p-0 sm:max-w-md'>
        <SheetHeader className='space-y-3 border-b px-5 py-4'>
          <SheetTitle className='flex items-center gap-2 text-base'>
            <FileText className='h-4 w-4' />
            <ContainerIdPrefix id={containerId} />
            <span dir='auto'>{containerLabel}</span>
          </SheetTitle>

          {totalCount > 0 && (
            <div className='space-y-2'>
              {/* Progress bar */}
              <div className='space-y-1'>
                <div className='flex items-center justify-between text-xs'>
                  <span className='text-muted-foreground'>
                    {completedCount}/{totalCount} {t('pages').toLowerCase()}
                  </span>
                  <span className='font-medium'>{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className='h-1.5' />
              </div>

              {/* Summary stats */}
              <div className='bg-muted/50 flex items-center justify-around rounded-md py-2 text-center'>
                <div>
                  <p className='text-lg font-semibold'>{totalFields}</p>
                  <p className='text-muted-foreground text-[10px] uppercase'>
                    {t('fields')}
                  </p>
                </div>
                <Separator orientation='vertical' className='h-8' />
                <div>
                  <p className='text-lg font-semibold text-emerald-600'>
                    {totalVerified}
                  </p>
                  <p className='text-muted-foreground text-[10px] uppercase'>
                    {t('statusVerified')}
                  </p>
                </div>
                <Separator orientation='vertical' className='h-8' />
                <div>
                  <p className='text-lg font-semibold'>
                    {totalFields - totalVerified}
                  </p>
                  <p className='text-muted-foreground text-[10px] uppercase'>
                    {t('pending')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </SheetHeader>

        {/* `[&_[data-slot=scroll-area-viewport]>div]:!block` — Radix renders
            the viewport's child as `display: table`, so it can measure content
            width for horizontal scrolling. A table box sizes to its content, so
            one long unbreakable value (`1002605941012810017`) widened it past
            the sheet and the expanded field list spilled out over the listing
            behind it. Forcing that wrapper back to `block` makes the rows obey
            the sheet's width — which is also what lets the ellipsis clipping
            below take effect, since it needs a bounded parent.
            Fixed here rather than in `components/ui/scroll-area.tsx`, which is
            shadcn-owned and must not be edited. */}
        <ScrollArea className='min-w-0 flex-1 overflow-auto [&_[data-slot=scroll-area-viewport]>div]:!block'>
          {isPending ? (
            <div className='flex items-center justify-center py-16'>
              <Loader2 className='text-muted-foreground h-6 w-6 animate-spin' />
            </div>
          ) : pageRows.length === 0 ? (
            <div className='flex flex-col items-center justify-center px-6 py-16 text-center'>
              <div className='bg-muted mb-4 rounded-full p-3'>
                <ScanText className='text-muted-foreground h-8 w-8' />
              </div>
              <p className='text-muted-foreground text-sm font-medium'>
                {t('noResults')}
              </p>
              <p className='text-muted-foreground mt-1 text-xs'>
                {t('noResultsHint')}
              </p>
              <PermissionGate permissions={['ocr.edit']}>
                <Button
                  onClick={handleStartOcr}
                  disabled={startMutation.isPending}
                  className='mt-4'
                  size='sm'
                >
                  {startMutation.isPending ? (
                    <Loader2 className='me-2 h-4 w-4 animate-spin' />
                  ) : (
                    <ScanText className='me-2 h-4 w-4' />
                  )}
                  {t('startOcr')}
                </Button>
              </PermissionGate>
            </div>
          ) : (
            <div className='divide-y'>
              {pageRows.map((row) => {
                const { result, page, pageIndex, fields } = row;
                const isResultAnchor = row.isFirstGroupForResult;
                const rowStatus = resolvePageRowState(row);
                const config =
                  rowStatus === 'no_data' || rowStatus === 'not_covered'
                    ? COVERAGE_CONFIG[rowStatus]
                    : STATUS_CONFIG[rowStatus];
                const StatusIcon = config.icon;
                const fieldCount = fields.length;
                const verifiedCount = fields.filter(
                  (f) => f.status === 'processed'
                ).length;
                // Rows are one-per-physical-page, so the page id IS the key.
                const rowKey = page.id;
                const isExpanded = expandedId === rowKey;
                const hasFields =
                  result.status === 'completed' && fieldCount > 0;
                const pageLabel = page.page_number
                  ? `${t('pages')} ${page.page_number}`
                  : `${t('pages')} ${pageIndex}`;
                // Which pages the owning RUN covers — `resolveOwnedPages` is
                // the same answer the results panel and the QA screen use, so
                // the duration line cannot claim pages the run does not own.
                const runPageRanges = isResultAnchor
                  ? formatPageRanges(
                      resolveOwnedPages(result).map((owned) => owned.pageNumber)
                    )
                  : '';

                return (
                  <Collapsible
                    key={rowKey}
                    open={isExpanded}
                    onOpenChange={(open) => setExpandedId(open ? rowKey : null)}
                  >
                    <div className='px-5 py-3.5'>
                      {/* Result header */}
                      <div className='flex items-start gap-3'>
                        {/* The page itself opens its results dialog — over this
                            sheet, so the operator never leaves the listing to
                            check one value against the scan. */}
                        <PageRowThumbnail
                          page={page}
                          batchId={result.batch_id ?? null}
                          label={pageLabel}
                          onOpen={() => setDialogPageId(page.id)}
                        />
                        <div
                          className={cn(
                            'mt-0.5 rounded-full border p-1.5',
                            config.className
                          )}
                        >
                          <StatusIcon
                            className={cn(
                              'h-3.5 w-3.5',
                              rowStatus === 'processing' && 'animate-spin'
                            )}
                          />
                        </div>
                        <div className='min-w-0 flex-1'>
                          <div className='flex items-center justify-between gap-2'>
                            <button
                              type='button'
                              onClick={() => setDialogPageId(page.id)}
                              className='min-w-0 truncate text-start text-sm font-medium hover:underline'
                            >
                              {pageLabel}
                            </button>
                            <Badge
                              variant='outline'
                              className={cn('text-[10px]', config.className)}
                            >
                              {rowStatus === 'processing'
                                ? t('statusProcessing')
                                : rowStatus === 'paused'
                                  ? t('statusPaused')
                                  : rowStatus === 'completed'
                                    ? t('statusCompleted')
                                    : rowStatus === 'no_data'
                                      ? t('statusNoData')
                                      : rowStatus === 'not_covered'
                                        ? t('statusNotCovered')
                                        : t('statusFailed')}
                            </Badge>
                          </div>

                          {(rowStatus === 'no_data' ||
                            rowStatus === 'not_covered') && (
                            <p className='text-muted-foreground mt-0.5 text-xs'>
                              {rowStatus === 'no_data'
                                ? t('pageReadNoData')
                                : t('pageNotCoveredHint')}
                            </p>
                          )}

                          {result.status === 'completed' && fieldCount > 0 && (
                            <p className='text-muted-foreground mt-0.5 text-xs'>
                              {verifiedCount}/{fieldCount} {t('fields')}{' '}
                              {t('statusVerified').toLowerCase()}
                            </p>
                          )}

                          {/* `duration_ms` is the RUN's, not this page's: one
                              run reads a whole batch of pages in ONE model call
                              and reports a single duration. Appended to the
                              anchor page's field count it read as "page 1 took
                              1m 37s" — so it gets its own line, and names the
                              pages it actually covers. */}
                          {isResultAnchor &&
                            result.duration_ms != null &&
                            runPageRanges !== '' && (
                              <p className='text-muted-foreground mt-0.5 text-[11px]'>
                                {t('processedIn', {
                                  duration: formatDuration(result.duration_ms)
                                })}{' '}
                                &middot;{' '}
                                {t('runCoversPages', { pages: runPageRanges })}
                              </p>
                            )}

                          {result.status === 'failed' && isResultAnchor && (
                            <div className='mt-2'>
                              <OcrErrorCard
                                resultId={result.id}
                                errorCode={result.error_code}
                                errorMessage={result.error_message}
                                compact
                              />
                            </div>
                          )}

                          {result.status === 'completed_with_errors' &&
                            isResultAnchor &&
                            result.failed_page_ranges &&
                            result.failed_page_ranges.length > 0 && (
                              <div className='mt-2'>
                                <FailedBatchesCallout
                                  ocrResultId={result.id}
                                  totalBatches={result.total_batches ?? 0}
                                  failedBatches={result.failed_batches}
                                  failedPageRanges={result.failed_page_ranges}
                                />
                              </div>
                            )}

                          {/* Action row */}
                          {(result.status === 'completed' ||
                            result.status === 'pending') && (
                            <div className='mt-2 flex items-center gap-1.5'>
                              <Button
                                variant='outline'
                                size='sm'
                                className='h-7 text-xs'
                                // Opens the panel on THIS page, not on page 1,
                                // and in-screen rather than through a route
                                // change (OMA-635). `pageIndex`, not
                                // `page.page_number` — the latter is the
                                // printed LABEL ("PG-002"), while the panel
                                // addresses pages by their 1-based position in
                                // the container's list, which is what
                                // `pageIndex` already is.
                                onClick={() =>
                                  handleOpenResultsView({
                                    kind: 'page',
                                    pageNumber: pageIndex
                                  })
                                }
                              >
                                <Eye className='me-1.5 h-3 w-3' />
                                {t('viewResults')}
                              </Button>

                              {hasFields && (
                                <CollapsibleTrigger asChild>
                                  <Button
                                    variant='ghost'
                                    size='sm'
                                    className='h-7 text-xs'
                                  >
                                    <ChevronDown
                                      className={cn(
                                        'me-1 h-3 w-3 transition-transform',
                                        isExpanded && 'rotate-180'
                                      )}
                                    />
                                    {fieldCount} {t('fields')}
                                  </Button>
                                </CollapsibleTrigger>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Expandable extracted data quick view */}
                      {hasFields && (
                        <CollapsibleContent>
                          <FieldQuickView
                            fields={fields}
                            resultId={result.id}
                            pageNumber={pageIndex}
                            onOpenField={handleOpenResultsView}
                          />
                        </CollapsibleContent>
                      )}
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Suggestion-run history (read-only) */}
        {registrationId != null && (
          <>
            <Separator />
            <div className='px-5 py-3'>
              <SuggestionHistoryPanel
                registrationId={registrationId}
                mode='readonly'
                enabled={containerId != null}
                collapsible
              />
            </div>
          </>
        )}

        {/* Footer actions */}
        {results.length > 0 && (
          <>
            <Separator />
            <div className='flex items-center gap-2 px-5 py-3'>
              <PermissionGate permissions={['ocr.edit']}>
                <Button
                  onClick={handleStartOcr}
                  disabled={startMutation.isPending}
                  variant='outline'
                  size='sm'
                  className='flex-1'
                >
                  {startMutation.isPending ? (
                    <Loader2 className='me-2 h-4 w-4 animate-spin' />
                  ) : (
                    <RotateCcw className='me-2 h-4 w-4' />
                  )}
                  {t('rerunOcr')}
                </Button>
              </PermissionGate>
            </div>
          </>
        )}
      </SheetContent>

      <PageResultsDialog
        row={dialogRow}
        containerId={containerId}
        containerLabel={containerLabel}
        totalPages={totalCount}
        onClose={() => setDialogPageId(null)}
      />

      {containerId != null && registrationId != null && (
        <OcrTemplatePoolDialog
          open={poolDialogOpen}
          onOpenChange={setPoolDialogOpen}
          containerId={containerId}
          registrationId={registrationId}
          containerLabel={containerLabel}
        />
      )}
    </Sheet>
  );
}
